// The realtime envelope (spec/09-realtime-protocol.md §2).
//
// Every frame on the gateway is one JSON object with these keys, and the payload `p` is validated
// against the schema for its `type`. The C# station client works from JSON Schema generated from
// this file, so a field renamed here is a field renamed on the station in the same pull request —
// which is the entire point of the schemas living in the contract.
//
// Unknown `type` values are ignored with a logged warning (forward compatibility) and unknown
// fields are ignored, so the envelope is not strict: a newer server must be able to talk to an
// older station.
import { z } from "../zod.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { ID_PREFIXES } from "../ids/schema.js";
import { ChannelSchema } from "./channel.js";
import { REALTIME_CLOSE_CODES } from "./constants.js";
/** `k`: what the frame is. */
export const REALTIME_KINDS = ['auth', 'cmd', 'ack', 'evt', 'sub', 'unsub', 'ok', 'err', 'ping'];
export const RealtimeKindSchema = z.enum(REALTIME_KINDS).openapi('RealtimeKind', {
    description: 'What a realtime frame is: a command, an event, an acknowledgement, and so on.',
});
/** `type`: `ns.event`, from the catalogue in spec/09 §6 — `session.lease`, `floor.station.updated`. */
export const REALTIME_MESSAGE_TYPE_PATTERN = /^[a-z][a-z0-9]*(?:\.[a-z][a-z0-9]*)+$/;
/**
 * `id` and `re`: a frame's own id, `msg_…` — or, on a command and on the `ack` that answers it, the
 * command's id, `cmd_…` (spec/09 §5.1: "the station replies `ack` with `re` = command id"). A command
 * is a `station_command` row before it is a frame, so the frame carries the row's id rather than a
 * second one the station would have to map back.
 */
export const REALTIME_FRAME_ID_PATTERN = new RegExp(`^(?:${ID_PREFIXES.message}|${ID_PREFIXES.stationCommand})_[0-7][0123456789abcdefghjkmnpqrstvwxyz]{25}$`);
export const RealtimeFrameIdSchema = z
    .string()
    .regex(REALTIME_FRAME_ID_PATTERN, 'Expected a message id (msg_…) or a command id (cmd_…)')
    .openapi('RealtimeFrameId', {
    description: 'A frame id: `msg_…`, or the `cmd_…` of the station command a `cmd` frame carries.',
    example: 'msg_01j8f7q0r2s3t4u5v6w7x8y9z0',
});
export const RealtimeMessageTypeSchema = z
    .string()
    .regex(REALTIME_MESSAGE_TYPE_PATTERN, 'Expected a message type like "session.lease"')
    .openapi('RealtimeMessageType', {
    description: 'A message type from the catalogue in spec/09 §6.',
    example: 'session.lease',
});
/**
 * The envelope, with the payload left open. A concrete message pairs this with the payload schema
 * for one `type`; see `realtimeMessage`.
 */
export const RealtimeEnvelopeSchema = z
    .object({
    k: RealtimeKindSchema,
    /** Unique per sender; used for acks and de-duplication. */
    id: RealtimeFrameIdSchema,
    type: RealtimeMessageTypeSchema,
    /** Stations only: the per-station sequence on events they send (spec/09 §5.2). */
    seq: z.int().min(0).optional(),
    /** The channel, for `evt`, `sub` and `unsub`. */
    ch: ChannelSchema.optional(),
    ts: IsoDateTimeSchema,
    /** The payload, validated against the schema for `type`. */
    p: z.looseObject({}).optional(),
    /** For `ack`, `ok` and `err`: the id of the frame being answered. */
    re: RealtimeFrameIdSchema.optional(),
})
    .openapi('RealtimeEnvelope', {
    description: 'Every frame on the realtime gateway is one of these (spec/09 §2).',
});
/**
 * The envelope for one message type, with its payload typed.
 *
 * ```ts
 * export const SessionLease = realtimeMessage('cmd', 'session.lease', SessionLeasePayloadSchema);
 * ```
 *
 * The catalogue in spec/09 §6 is filled in by the features that own each message; this is the
 * primitive they all use, so that no two of them spell the envelope differently.
 */
export function realtimeMessage(kind, type, payload, componentName) {
    if (!REALTIME_MESSAGE_TYPE_PATTERN.test(type)) {
        throw new TypeError(`"${type}" is not a realtime message type; expected something like "session.lease".`);
    }
    const schema = RealtimeEnvelopeSchema.extend({
        k: z.literal(kind),
        type: z.literal(type),
        p: payload,
    });
    return componentName ? schema.openapi(componentName) : schema;
}
/**
 * The close codes (spec/09 §9). Each means exactly one thing, and a client's reaction is decided by
 * the code alone — which is why a slow consumer has a code of its own rather than borrowing one.
 *
 *   1000  normal                 the client closed, or the server had nothing more to say
 *   1001  going away             this node is draining (`server.draining` came first): reconnect after
 *                                the suggested delay
 *   1013  try again later        the socket's send buffer passed 4 MiB — a slow consumer (spec/09 §8):
 *                                reconnect with the usual jittered backoff and resync from a snapshot.
 *                                spec/09 §8 says the socket is closed and §9 had no code for it; this is
 *                                RFC 6455's registered "Try Again Later" (INFRA-18, proposed in spec/23 §6)
 *   4401  unauthenticated        no `auth` within 5 s, a bad or expired token, or a missed refresh: get a
 *                                new token and reconnect
 *   4403  forbidden              station unpaired, member removed, person banned from the Platform:
 *                                stations go to the pairing screen; consoles and apps sign out
 *   4409  replaced               a newer connection for the same station: do not reconnect from here
 *   4413  frame too large        over 64 KiB (256 KiB for WebRTC signalling): report as a bug
 *   4429  rate limited           over 20 frames/s sustained or a burst of 100: back off 30 s
 */
export { REALTIME_CLOSE_CODES };
/**
 * The numbers spec/09 fixes, in one place, so the gateway, the clients and the tests read the same
 * ones. Durations in milliseconds unless the name says otherwise.
 */
export const REALTIME_LIMITS = {
    /** §1: the largest frame. */
    maxFrameBytes: 64 * 1024,
    /** §1: the largest WebRTC signalling frame (`station.webrtc.signal`, and a member's signal). */
    maxSignalFrameBytes: 256 * 1024,
    /** §1: the first frame must be `auth`, within this. */
    authTimeoutMs: 5_000,
    /** §1: `auth.expiring` is sent this long before the realtime token expires. */
    refreshLeadMs: 60_000,
    /** §1: the realtime token's lifetime. */
    tokenLifetimeSeconds: 300,
    /** §1: the server pings every 20 s … */
    pingIntervalMs: 20_000,
    /** … and drops a client that misses two. */
    missedPongsBeforeDrop: 2,
    /** §8: per connection, sustained … */
    framesPerSecond: 20,
    /** … and the burst the bucket holds. */
    frameBurst: 100,
    /** §8: floor updates for one station inside this window are merged, latest wins. */
    coalesceMs: 250,
    /** §8: past this much unsent, a socket gets merged updates only. */
    slowConsumerBytes: 1024 * 1024,
    /** §8: past this much unsent, the socket is closed (`1013`). */
    maxBufferedBytes: 4 * 1024 * 1024,
    /** §5.2: station events are forwarded to the api every 100 ms … */
    forwardIntervalMs: 100,
    /** … or every 50 events, whichever comes first. */
    forwardBatchEvents: 50,
    /** §5.1: a station acknowledges a command within this. */
    commandAckTimeoutMs: 10_000,
    /** §5.1: the station de-duplicates commands by id, keeping this many. */
    stationSeenCommands: 500,
};
//# sourceMappingURL=envelope.js.map